import { TemplateField } from 'app/interfaces/template-field.interface';

export interface LinkField {
  columnName: string | null;
  label: string;
}

const FIELD_REGEX = /\{field\s([^}]+)\}/g;
const ENV_REGEX = /\{env(?:\s+([^}]*))?\}/g;
const _LINK_TAG_REGEX = /\{link\s[^}]+\}/g;

const DEFAULT_ENV_VALUES = ['dev', 'ppd', 'prd'];

function parseAttrs(attrString: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  // Support both double-quoted and single-quoted attribute values
  const regex = /(\w+)=(?:"([^"]*)"|'([^']*)')/g;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(attrString)) !== null) {
    attrs[m[1]] = m[2] ?? m[3] ?? '';
  }
  return attrs;
}

function parseEnvValues(rawValues: string): string[] {
  return rawValues
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}

export function parseTemplateFields(sql: string): TemplateField[] {
  const fields = new Map<string, TemplateField>();
  let match: RegExpExecArray | null;

  FIELD_REGEX.lastIndex = 0;
  while ((match = FIELD_REGEX.exec(sql)) !== null) {
    const attrs = parseAttrs(match[1]);
    const name = attrs['name'];
    if (!name || fields.has(name)) {
      continue;
    }

    fields.set(name, {
      type: (attrs['type'] as 'text' | 'textlist' | 'numberlist' | 'date' | 'number') ?? 'text',
      name,
      label: attrs['label'] ?? null,
      default: attrs['default'] ?? null,
      description: attrs['description'] ?? null,
      values: null,
      prefix: attrs['prefix'] ?? null,
      suffix: attrs['suffix'] ?? null,
    });
  }

  ENV_REGEX.lastIndex = 0;
  while ((match = ENV_REGEX.exec(sql)) !== null) {
    if (fields.has('env')) {
      continue;
    }
    const attrs = match[1] ? parseAttrs(match[1]) : {};
    const values = attrs['values'] ? parseEnvValues(attrs['values']) : DEFAULT_ENV_VALUES;
    const defaultVal = attrs['default'] ?? values.at(-1) ?? DEFAULT_ENV_VALUES.at(-1);

    fields.set('env', {
      type: 'env',
      name: 'env',
      label: attrs['label'] ?? null,
      default: defaultVal,
      description: null,
      values,
      prefix: null,
      suffix: null,
    });
  }

  return Array.from(fields.values());
}

/**
 * Parses {link} tags from the SQL template and maps each to its column alias and label.
 * An explicit AS alias takes precedence; otherwise the label is sanitized into an alias
 * (matching the backend's auto-alias logic so no AS clause is required in the template).
 */
export function parseLinkFields(sql: string): LinkField[] {
  const fields: LinkField[] = [];
  const regex = /\{link\s([^}]+)\}[ \t]*(?:(?:AS|as)\s+(\w+))?/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(sql)) !== null) {
    const attrs = parseAttrs(match[1]);
    const label = attrs['label'] || attrs['url'] || '';
    const columnName =
      match[2] ??
      (attrs['label']
        ? attrs['label']
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
        : null);
    fields.push({ columnName, label });
  }

  return fields;
}

export function buildFieldTag(field: {
  type: string;
  name: string;
  label: string;
  default: string;
  description: string;
  prefix?: string;
  suffix?: string;
}): string {
  let tag = `{field type="${field.type}" name="${field.name}"`;
  if (field.label) {
    tag += ` label="${field.label}"`;
  }
  if (field.default) {
    tag += ` default="${field.default}"`;
  }
  if (field.description) {
    tag += ` description="${field.description}"`;
  }
  if (field.prefix) {
    tag += ` prefix="${field.prefix}"`;
  }
  if (field.suffix) {
    tag += ` suffix="${field.suffix}"`;
  }
  tag += '}';
  return tag;
}

export function buildLinkTag(link: { url: string; label: string }): string {
  let tag = `{link url="${link.url}"`;
  if (link.label) {
    tag += ` label="${link.label}"`;
  }
  tag += '}';
  return tag;
}

export function buildEnvTag(values: string[] = DEFAULT_ENV_VALUES): string {
  return `{env values="${values.join(',')}"}`;
}
